// Auto-Grow-Strategien fuer Method-Blocks. Jede Strategie reagiert anders auf
// "diese Region ist voll" — siehe Spec docs/mindCoder/specs/2026-05-26-methoden-workshop-reife-design.md.
//
// Muss innerhalb yMap.doc.transact aufgerufen werden — sonst sehen Children einen
// halb-resized Zustand.

import {
  type CanvasObject,
  type MethodId,
  type RegionObj,
  isCanvasObject,
} from "@ms-whiteboard/shared";
import type * as Y from "yjs";
import { cascadeResizeRegions } from "./cascade.js";

export type GrowStrategy = "proportional" | "block-height" | "lane-local" | "region-local";

/** Auto-Grow-Strategie pro Methode (Spec §54-59). */
const METHOD_GROW_STRATEGY: Record<MethodId, GrowStrategy> = {
  moscow: "proportional",
  "requirements-cluster": "proportional",
  "impact-effort": "proportional",
  "pain-dependency-cluster": "proportional",
  "process-high-level": "block-height",
  sipoc: "block-height",
  "process-swimlane": "lane-local",
  "context-target-light": "region-local",
};

export function growStrategyForMethod(methodId: string): GrowStrategy {
  return (METHOD_GROW_STRATEGY as Record<string, GrowStrategy>)[methodId] ?? "proportional";
}

export function growRegion(
  yMap: Y.Map<unknown>,
  region: RegionObj,
  strategy: GrowStrategy,
  deltaPx: number,
): void {
  const blockRaw = yMap.get(region.blockId);
  if (!isCanvasObject(blockRaw) || blockRaw.type !== "block") return;
  const block = blockRaw;

  switch (strategy) {
    case "proportional":
      growProportional(yMap, block, deltaPx);
      return;
    case "block-height":
      growBlockHeight(yMap, block, deltaPx);
      return;
    case "lane-local":
      growLaneLocal(yMap, block, region, deltaPx);
      return;
    case "region-local":
      growRegionLocal(yMap, region, deltaPx);
      return;
  }
}

function growProportional(yMap: Y.Map<unknown>, block: CanvasObject, deltaPx: number): void {
  if (block.type !== "block") return;
  const oldBox = { x: block.x, y: block.y, width: block.width, height: block.height };
  const newBox = { ...oldBox, height: oldBox.height + deltaPx };
  yMap.set(block.id, { ...block, height: newBox.height });
  cascadeResizeRegions(yMap, block.id, oldBox, newBox);
}

function growBlockHeight(yMap: Y.Map<unknown>, block: CanvasObject, deltaPx: number): void {
  if (block.type !== "block") return;
  // Block waechst in Hoehe, alle Children-Regions skalieren proportional
  // (Spalten-Breite bleibt durch das skalierende Sub-Layout des cascadeResizeRegions
  // konstant, weil X-Breite unveraendert ist).
  const oldBox = { x: block.x, y: block.y, width: block.width, height: block.height };
  const newBox = { ...oldBox, height: oldBox.height + deltaPx };
  yMap.set(block.id, { ...block, height: newBox.height });
  cascadeResizeRegions(yMap, block.id, oldBox, newBox);
}

function growLaneLocal(
  yMap: Y.Map<unknown>,
  block: CanvasObject,
  targetLane: RegionObj,
  deltaPx: number,
): void {
  if (block.type !== "block") return;
  // 1. Block-Hoehe +delta
  // 2. targetLane.height += delta
  // 3. alle Lanes deren y >= targetLane.y + targetLane.height bekommen y += delta
  yMap.set(block.id, { ...block, height: block.height + deltaPx });
  yMap.set(targetLane.id, { ...targetLane, height: targetLane.height + deltaPx });

  const cutoffY = targetLane.y + targetLane.height;
  for (const value of yMap.values()) {
    if (!isCanvasObject(value)) continue;
    if (value.type !== "region") continue;
    if (value.blockId !== block.id) continue;
    if (value.id === targetLane.id) continue;
    if (value.y >= cutoffY) {
      yMap.set(value.id, { ...value, y: value.y + deltaPx });
    }
  }

  // Children dieser Lanes mitnehmen: alle Objekte mit parentRegionId der pushen-Lanes
  for (const value of yMap.values()) {
    if (!isCanvasObject(value)) continue;
    if (value.type === "region" || value.type === "block") continue;
    if (!value.parentRegionId) continue;
    const parent = yMap.get(value.parentRegionId);
    if (!isCanvasObject(parent) || parent.type !== "region") continue;
    if (parent.blockId !== block.id) continue;
    if (parent.y >= cutoffY + deltaPx) {
      yMap.set(value.id, { ...value, y: value.y + deltaPx });
    }
  }
}

function growRegionLocal(yMap: Y.Map<unknown>, region: RegionObj, deltaPx: number): void {
  // Nur die Region selbst waechst. Andere Regions unveraendert. Block unveraendert
  // (Region kann ueber Block-Bounds hinausragen — Caller-Methode entscheidet ob das OK ist).
  yMap.set(region.id, { ...region, height: region.height + deltaPx });
}
